/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useContext, createContext, useEffect, useRef} from 'react'
import PropTypes from 'prop-types'
import {
    Box,
    Button,
    Flex,
    Stack,
    Text,
    useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {CheckIcon} from '../icons'
import {remCalc} from '../../theme/_utils'
import LoadingSpinner from '../loading-spinner'
const ToggleCardContext = createContext()

/**
 * A card-like box that renders one of two states: 'edit' and 'summary'. It takes a single
 * `ToggleCardSummary` and `ToggleCardEdit` component as children and renders one or the
 * other depending on the `editing` prop. See `app/pages/checkout` for example.
 */
export const ToggleCard = ({
    id,
    title,
    editing,
    disabled,
    onEdit,
    editLabel,
    isLoading,
    children,
    variant,
    isSuccessIconVisible,
    customMarginBottom,
    isAccount = false,
    ...props
}) => {
    const titleRef = useRef()

    useEffect(() => {
        if (editing && titleRef.current) {
            titleRef.current.focus()
        }
    }, [editing])

    const styles = useMultiStyleConfig('Checkout', {variant})

    return (
        <ToggleCardContext.Provider value={{editing, disabled}}>
            <Box
                layerStyle="card"
                rounded={0}
                data-testid={`sf-toggle-card-${id}`}
                position="relative"
                paddingBottom={editing || disabled ? remCalc(24) : remCalc(0)}
                {...styles.toggleCardBox}
                {...props}
            >
                <Stack {...styles.ToggleCardStack}>
                    <Flex justify="space-between" {...styles.gapCard}>
                        {title && (
                            <Flex
                                ref={titleRef}
                                alignItems="center"
                                /* {...styles.toggleIconFlexBraun} */
                                mb={
                                    isSuccessIconVisible
                                        ? customMarginBottom
                                            ? remCalc(24)
                                            : remCalc(16)
                                        : remCalc(0)
                                }
                            >
                                {isSuccessIconVisible && (
                                    <Box {...styles.toggleIconBox}>
                                        <CheckIcon {...styles.toggleCheckIcon} />
                                    </Box>
                                )}
                                <Text
                                    color={isSuccessIconVisible ? 'text.default' : 'text.secondary'}
                                    textStyle="body-medium"
                                >
                                    {title}
                                </Text>
                            </Flex>
                        )}

                        {!isAccount && !editing && !disabled && onEdit && editLabel && (
                            <Button
                                {...styles.editButton}
                                variant="text-button"
                                onClick={onEdit}
                                aria-label={editLabel}
                            >
                                {editLabel}
                            </Button>
                        )}
                    </Flex>

                    <Box data-testid={`sf-toggle-card-${id}-content`} {...styles.boxContent}>
                        {children}
                    </Box>
                    {isAccount && !editing && !disabled && onEdit && editLabel && (
                        <Button
                            {...styles.editButton}
                            variant="text-button"
                            onClick={onEdit}
                            aria-label={editLabel}
                        >
                            {editLabel}
                        </Button>
                    )}
                </Stack>

                {isLoading && editing && <LoadingSpinner />}
            </Box>
        </ToggleCardContext.Provider>
    )
}

export const ToggleCardEdit = ({children}) => {
    const {editing} = useContext(ToggleCardContext)
    return editing ? children : null
}

export const ToggleCardSummary = ({children}) => {
    const {editing, disabled} = useContext(ToggleCardContext)
    return !editing && !disabled ? children : null
}

ToggleCard.propTypes = {
    id: PropTypes.string,
    title: PropTypes.any,
    editLabel: PropTypes.any,
    editing: PropTypes.bool,
    isAccount: PropTypes.bool,
    isLoading: PropTypes.bool,
    disabled: PropTypes.bool,
    onEdit: PropTypes.func,
    children: PropTypes.any,
    isSuccessIconVisible: PropTypes.bool,
    customMarginBottom: PropTypes.bool,
    variant: PropTypes.oneOf([
        'default',
        'profile-card',
        'edit-card',
        'shipping',
        'options',
        'checkout',
    ]),
}

ToggleCardEdit.propTypes = {
    children: PropTypes.any,
}

ToggleCardSummary.propTypes = {
    children: PropTypes.any,
}
